import { Injectable, OnDestroy } from '@angular/core';
import { environment } from '@env/environment';
import isEqual from 'lodash-es/isEqual';
import { debounceTime, merge, Observable, of, shareReplay, Subscription, switchMap, tap } from 'rxjs';
import { unsubscribeFromAll } from '../util/unsubscribeFromAll';
import { EventBusService } from './event-bus.service';

/**
 * Options for configuring the caching behavior.
 */
export interface CacheOptions<T = any> {
    // Names of caches to exclude from caching.
    blacklist?: string[];
    // Names of caches to include in caching. If specified, only these caches will be used.
    whitelist?: string[];
    // A namespace to append to all cache names to avoid collisions.
    namespace?: string;
    // Function to determine whether to store the result in the cache.
    shouldStoreInCache?: (value: T) => boolean;
    // Time-to-live in milliseconds. If set, cache entries expire after this duration.
    ttl?: number;
}

/**
 * Represents a cache entry with optional parameters and cached data.
 */
interface CacheEntry<T> {
    params?: any[];
    cachedData: T;
    timestamp?: number;
}

@Injectable({
    providedIn: 'root',
})
export class CachingService implements OnDestroy {
    private subscriptions: { [key: string]: Subscription } = {};
    private cache: { [key: string]: CacheEntry<any>[] } = {};
    private requestCache: Map<string, Observable<any>> = new Map();

    constructor(private eventBus: EventBusService) {
        this.subscriptions.eventBus = merge(
            this.eventBus.getObservableForEventStream('logout'),
            this.eventBus.getObservableForEventStream('tokenChange'),
        ).subscribe(() => {
            this.cache = {};
        });
    }

    public ngOnDestroy(): void {
        unsubscribeFromAll(this.subscriptions);
    }

    /**
     * Wrap any http call with this function to cache the result. WARNING: Ensure that the function around which you wrap around this around
     * actually completes its returned observable.
     * @param {string} cacheName - The name of the cache. Use the same name for different calls that should share the same cache.
     * @param {Function} callback - The function to wrap around.
     * @param {*[]} params - The parameters to pass to the callback. This is an array of parameters, not a single parameter.
     * @param {object} [options] - The options for the caching system. See the CacheOptions interface for more information.
     * @returns An observable emitting the cached or fetched data.
     */
    public wrapCache<T>(
        cacheName: string,
        callback: (...args: any[]) => Observable<T>,
        params: any[] = [],
        options?: CacheOptions<T>,
    ): Observable<T> {
        const shouldUseCache = this.shouldUseCache(cacheName, options);

        if (!options?.shouldStoreInCache) {
            options = {
                ...options,
                shouldStoreInCache: () => true,
            };
        }

        cacheName = this.appendNamespace(cacheName, options);

        if (!shouldUseCache) {
            return callback.apply(this, params);
        }

        if (this.cache[cacheName]) {
            if (params.length === 0) {
                return this.handleNoParamsCache(cacheName, callback, options);
            }

            const cacheIndex = this.findCacheElementIndex(cacheName, params);

            if (cacheIndex !== -1) {
                const entry = this.cache[cacheName][cacheIndex];

                if (this.isCacheEntryExpired(entry, options?.ttl)) {
                    this.cache[cacheName].splice(cacheIndex, 1);
                } else {
                    return of(entry.cachedData as T);
                }
            }

            const callbackObservable = callback.apply(this, params).pipe(
                tap((values: T) => {
                    if (options!.shouldStoreInCache!.call(this, values)) {
                        this.addOrUpdateCache(cacheName, params, values, options?.ttl);
                    }
                }),
            );

            return this.getCallbackFromRequestCache(cacheName, callbackObservable, params);
        }

        // The first element must exist, even if it's undefined since
        // it will be replaced with the value for no-param calls
        this.cache[cacheName] = [undefined as any];

        const callbackObservable = callback.apply(this, params).pipe(
            tap((values: T) => {
                if (options!.shouldStoreInCache!.call(this, values)) {
                    if (params.length > 0) {
                        this.addOrUpdateCache(cacheName, params, values, options?.ttl);
                    } else {
                        this.cache[cacheName][0] = {
                            params: undefined,
                            cachedData: values,
                            timestamp: options?.ttl ? Date.now() : undefined,
                        };
                    }
                }
            }),
        );

        return this.getCallbackFromRequestCache(cacheName, callbackObservable, params);
    }

    /**
     * Checks if a cache entry has expired based on its timestamp and the provided TTL.
     * @param entry - The cache entry to check.
     * @param ttl - The time-to-live in milliseconds.
     * @returns True if the entry has expired; otherwise, false.
     */
    private isCacheEntryExpired(entry: CacheEntry<any>, ttl?: number): boolean {
        if (!ttl || !entry.timestamp) {
            return false;
        }
        return Date.now() - entry.timestamp > ttl;
    }

    /**
     * Adds a new cache entry or updates an existing one.
     * @param cacheName - The name of the cache.
     * @param params - The parameters associated with the cache entry.
     * @param values - The data to cache.
     * @param ttl - Optional time-to-live in milliseconds.
     */
    private addOrUpdateCache<T>(cacheName: string, params: any[], values: T, ttl?: number): void {
        const existingIndex = this.findCacheElementIndex(cacheName, params);

        const entry: CacheEntry<T> = {
            params: params,
            cachedData: values,
            timestamp: ttl ? Date.now() : undefined,
        };

        if (existingIndex !== -1) {
            this.cache[cacheName][existingIndex] = entry;
        } else {
            this.cache[cacheName].push(entry);
        }
    }

    /**
     * Finds the index of the cache element with the same params
     * @param {string} cacheName - The name of the cache in which to search
     * @param {unknown[]} params - The params associated with the cache element
     * @returns The index of the matching cache entry, or -1 if not found.
     * @private
     */
    private findCacheElementIndex(cacheName: string, params: any[]): number {
        for (let i = 1; i < this.cache[cacheName]?.length; i++) {
            const cacheElement = this.cache[cacheName][i];

            if (!cacheElement.params || cacheElement.params.length !== params.length) {
                continue;
            }

            if (isEqual(cacheElement.params, params)) {
                if (environment.dev) {
                    console.warn(`Cache hit for ${cacheName} with params`, params);
                }

                return i;
            }
        }

        return -1;
    }

    /**
     * Handles caching for functions without parameters.
     * @param cacheName - The name of the cache.
     * @param callback - The function to execute and cache.
     * @param options - Configuration options for caching.
     * @returns An observable emitting the cached or fetched data.
     */
    private handleNoParamsCache<T>(
        cacheName: string,
        callback: (...args: any[]) => Observable<T>,
        options?: CacheOptions<T>,
    ): Observable<T> {
        const entry = this.cache[cacheName][0];

        if (entry) {
            if (this.isCacheEntryExpired(entry, options?.ttl)) {
                this.cache[cacheName][0] = undefined as any;
            } else {
                if (!environment.production) {
                    console.log(`Cache hit for ${cacheName} with no params`);
                }
                return of(entry.cachedData as T);
            }
        }

        const callbackObservable = callback.apply(this, []).pipe(
            tap((values: T) => {
                if (options!.shouldStoreInCache!.call(this, values)) {
                    this.cache[cacheName][0] = {
                        params: undefined,
                        cachedData: values,
                        timestamp: options?.ttl ? Date.now() : undefined,
                    };
                }
            }),
        );

        return this.getCallbackFromRequestCache(cacheName, callbackObservable, []);
    }

    /**
     * Retrieves an observable from the request cache or creates a new one.
     * @param cacheName - The name of the cache.
     * @param callbackObservable - The observable to execute and cache.
     * @param params - The parameters associated with the request.
     * @returns The cached or new observable.
     */
    private getCallbackFromRequestCache<T>(cacheName: string, callbackObservable: Observable<T>, params: any[]): Observable<T> {
        const requestCacheKey = params.length > 0 ? `${cacheName}_${JSON.stringify(params)}` : cacheName;

        if (this.requestCache.has(requestCacheKey)) {
            return this.requestCache.get(requestCacheKey)! as Observable<T>;
        }

        // Create the observable with debounceTime
        const observable = of(requestCacheKey).pipe(
            debounceTime(5),
            switchMap(() => callbackObservable),
            shareReplay(1), // This will ensure that if the observable is subscribed to more than once, the result is shared.
        );

        this.addToRequestCache(requestCacheKey, observable);

        observable.subscribe({
            complete: this.deleteFromRequestCache.bind(this, requestCacheKey),
            error: this.deleteFromRequestCache.bind(this, requestCacheKey),
        });

        return observable;
    }

    /**
     * Adds an observable to the request cache.
     * @param cacheName - The name of the cache.
     * @param observable - The observable to cache.
     */
    private addToRequestCache<T>(cacheName: string, observable: Observable<T>) {
        // console.info('%cAdding%c to request cache %c' + cacheName, 'color: green;', 'color: black;', 'color: blue;');
        this.requestCache.set(cacheName, observable);
    }

    /**
     * Removes an observable from the request cache.
     * @param cacheName - The name of the cache.
     */
    private deleteFromRequestCache(cacheName: string): void {
        // console.info('%cDeleting%c from request cache %c' + cacheName, 'color: red;', 'color: black;', 'color: blue;');
        this.requestCache.delete(cacheName);
    }

    /**
     * Determines whether caching should be used based on the whitelist and blacklist.
     * @param cacheName - The name of the cache.
     * @param options - Configuration options for caching.
     * @returns True if caching should be used; otherwise, false.
     */
    private shouldUseCache(cacheName: string, options?: CacheOptions<any>): boolean {
        let isInWhitelist: boolean;
        let isInBlacklist: boolean;

        if (options) {
            const hasWhitelist = options.whitelist && options.whitelist.length > 0;
            const hasBlacklist = options.blacklist && options.blacklist.length > 0;

            if (hasWhitelist) {
                isInWhitelist = options.whitelist!.includes(cacheName);
            } else {
                isInWhitelist = true;
            }

            if (hasBlacklist) {
                isInBlacklist = options.blacklist!.includes(cacheName);
            } else {
                isInBlacklist = false;
            }

            return isInWhitelist && !isInBlacklist;
        }

        return true;
    }

    /**
     * Appends the namespace to the cacheName. Add other options here if needed
     * @param {string} cacheName
     * @param {object} [options]
     * @returns The namespaced cache name.
     * @private
     */
    private appendNamespace(cacheName: string, options?: CacheOptions<any>): string {
        return options?.namespace ? `${options.namespace}-${cacheName}` : cacheName;
    }
}
